import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

// Clave pública VAPID para Web Push
const VAPID_PUBLIC_KEY = 'BBqMp2rXFHHoprbtaQGDUTKI3WPak1oRzi-b6_DlqxTGikCsqyRYhzHUa6wxP6MwtqjqyqeV1WC3R--0tKo2lDw';

import { isMedianApp, getOneSignalInfo } from '../lib/median';

export default function PushNotificationManager() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Si estamos en la App Nativa (Median)
    if (isMedianApp()) {
      setIsSupported(true);
      registerOneSignal();
    }
    // Si estamos en Web
    else if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      checkSubscription();
    } else {
      setIsSupported(false);
    }
  }, [user]);

  const registerOneSignal = async () => {
    if (!user) return;

    try {
      // DEBUG: Avisar que intentamos registrar en modo nativo
      // toast('📱 Iniciando registro OneSignal...', { icon: '🤖', duration: 2000 });

      const info = await getOneSignalInfo();

      if (info && info.userId) {
        // toast.success(`ID OneSignal: ${info.userId.slice(0, 5)}...`);
        console.log('OneSignal Info Received:', info);

        // Guardar Player ID en Supabase
        // Usamos un formato especial para distinguir de WebPush normal
        const oneSignalSub = {
          type: 'onesignal',
          player_id: info.userId,
          push_token: info.pushToken,
          device_type: navigator.userAgent.includes('iPhone') ? 'ios' : 'android'
        };

        const { error } = await supabase
          .from('push_subscriptions')
          .upsert({
            user_id: user.id,
            subscription: JSON.stringify(oneSignalSub)
          }, { onConflict: 'user_id' });

        if (!error) {
          setIsSubscribed(true);
          console.log('OneSignal registrado en BD correctamente');
          // toast.success('🔔 Notificaciones Activadas (Nativo)');
        } else {
          console.error('Error guardando en Supabase:', error);
          toast.error('Error guardando suscripción');
        }
      } else {
        // Si entra aqui es que getOneSignalInfo devolvió null
        console.log('No se obtuvo info de OneSignal (aún)');
        // toast.error('No se detectó OneSignal ID');
      }
    } catch (err) {
      console.error('Error registrando OneSignal:', err);
      toast.error('Error interno OneSignal');
    }
  };

  const checkSubscription = async () => {
    try {
      // Registrar o recuperar el service worker
      const registration = await navigator.serviceWorker.ready;

      // Obtener la suscripción actual
      const existingSubscription = await registration.pushManager.getSubscription();

      if (existingSubscription) {
        setSubscription(existingSubscription);
        setIsSubscribed(true);
      } else {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Error al verificar la suscripción:', error);
    }
  };

  const subscribeUser = async () => {
    if (!user) return;

    // Si es App Nativa, el registro suele ser automático o gestionado por registerOneSignal
    if (isMedianApp()) {
      registerOneSignal();
      return;
    }

    try {
      // Solicitar permiso para notificaciones
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        toast.error('Se requiere permiso para enviar notificaciones');
        return;
      }

      // Registrar el service worker si no está registrado
      await navigator.serviceWorker.register('/sw.js');
      const registration = await navigator.serviceWorker.ready;

      // Crear una nueva suscripción
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Guardar la suscripción en la base de datos
      const { error } = await supabase
        .from('push_subscriptions')
        .insert({
          user_id: user.id,
          subscription: JSON.stringify(subscription)
        });

      if (error) throw error;

      setSubscription(subscription);
      setIsSubscribed(true);
      toast.success('Notificaciones activadas correctamente');
    } catch (error) {
      console.error('Error al suscribirse a notificaciones push:', error);
      toast.error('Error al activar notificaciones');
    }
  };

  const unsubscribeUser = async () => {
    if (!user || !subscription) return;

    try {
      // Cancelar la suscripción
      await subscription.unsubscribe();

      // Eliminar la suscripción de la base de datos
      const { error } = await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', user.id)
        .eq('subscription', JSON.stringify(subscription));

      if (error) throw error;

      setSubscription(null);
      setIsSubscribed(false);
      toast.success('Notificaciones desactivadas correctamente');
    } catch (error) {
      console.error('Error al cancelar la suscripción:', error);
      toast.error('Error al desactivar notificaciones');
    }
  };

  // Función auxiliar para convertir la clave VAPID a Uint8Array
  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  };

  // No renderizar nada visible, este componente solo maneja la lógica
  return null;
}